import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, theme } from 'antd';
import zhTW from 'antd/locale/zh_TW';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import App from './App';
import './index.css';

// 日期選擇器的月份／星期用繁中（antd 的 locale 不會自動切換 dayjs）
dayjs.locale('zh-tw');

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={zhTW}
        theme={{
          algorithm: theme.defaultAlgorithm,
          token: {
            colorPrimary: '#dd7900',
            colorLink: '#dd7900',
            colorLinkHover: '#f08c1a',
            borderRadius: 8,
            fontFamily: "'Noto Sans TC', system-ui, -apple-system, sans-serif",
            colorBgLayout: '#f4f5f7',
            colorTextHeading: '#1f2733',
            colorText: '#374151',
            colorTextSecondary: '#6b7280',
            controlHeight: 36,
            // 柔和的中性外框，避免黑線過重
            colorBorderSecondary: 'rgba(15,23,42,0.07)',
            boxShadowTertiary: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)',
          },
          components: {
            // 深 slate 側欄，與品牌橘 #dd7900 互補、專業耐看；header 維持白底加陰影。
            Layout: { siderBg: '#1f2733', headerBg: '#ffffff', bodyBg: '#f4f5f7' },
            // 卡片：較大圓角 + 細外框 + 一致內距，建立乾淨的容器節奏。
            Card: {
              borderRadiusLG: 12,
              colorBorderSecondary: 'rgba(15,23,42,0.07)',
              headerFontSize: 15,
              headerHeight: 52,
              paddingLG: 20,
              boxShadowTertiary: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)',
            },
            // 表格：表頭低調、列高舒適、hover 柔和橘暈。
            Table: {
              borderRadiusLG: 12,
              headerBg: '#f8f9fb',
              headerColor: '#6b7280',
              headerSplitColor: 'transparent',
              rowHoverBg: '#fdf6ec',
              cellPaddingBlock: 14,
              colorBorderSecondary: 'rgba(15,23,42,0.06)',
            },
            // 按鈕：主要按鈕加一點立體感與品牌 hover 提亮。
            Button: {
              fontWeight: 500,
              primaryShadow: '0 1px 2px rgba(221,121,0,0.28)',
              defaultShadow: 'none',
              colorPrimaryHover: '#ef8f12',
              colorPrimaryActive: '#c46c00',
            },
            Input: { activeShadow: '0 0 0 3px rgba(221,121,0,0.12)' },
            Select: { activeOutlineColor: 'rgba(221,121,0,0.12)' },
            Tag: { borderRadiusSM: 6 },
            Menu: {
              // 深色側欄選單：未選取低調、hover 提亮、選取用品牌橘 accent。
              darkItemBg: '#1f2733',
              darkSubMenuItemBg: '#1a212b',
              darkItemColor: 'rgba(255,255,255,0.72)',
              darkItemHoverColor: '#ffffff',
              darkItemHoverBg: 'rgba(255,255,255,0.08)',
              darkItemSelectedBg: '#dd7900',
              darkItemSelectedColor: '#ffffff',
              itemHeight: 44,
              itemMarginInline: 8,
              itemBorderRadius: 8,
            },
          },
        }}
      >
        <App />
      </ConfigProvider>
    </QueryClientProvider>
  </StrictMode>,
);
